import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";

import { useCallback, useEffect, useRef, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";

import { green, grey } from "@mui/material/colors";
import { GlobalState } from "context/GlobalContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { downLoadTableColumn, publishTableColumn, selectedOpenCostingPath } from "components/PimDashboard/data";
import {
	applyDiscountingCol,
	costingUrlKey,
	CreatedAtColumn,
	filterRequiredStyleColumns,
	finalTotalColumn,
	poCostColumn,
	supplierColumn,
	totalQtyColumn,
} from "../CreateOpenCosting/data";
import { converAttrToDatagridCol, createdByColumns, imageTableColumn, publishColumns, styleImageColumn } from "data/globalData";
import { FileDownloadRounded, FilterAltRounded, PlaylistAddRounded } from "@mui/icons-material";
import FileSaver from "file-saver";
import FilterModal from "./FilterModal/FilterModal";
import AddColumnModal from "./AddColumnModal/AddColumnModal";
import { allowAccess } from "utils/userAccessFun";
import { custom, departments } from "data/authData";
import { AuthState } from "context/AuthContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { generateCostingDetailsExcel, lsFilterkeyStr } from "./data";
import _ from "lodash";
import { PimDashboardState } from "context/PimDashboardContext";
import moment from "moment";
import DataExportModal from "./DataExportModal/DataExportModal";

export function setStylesCustomFun(prev, newRows) {
	const map = {};

	const result = [...prev, ...newRows]?.reduce((acc, row) => {
		if (map[row.style_ref_no]) return acc;
		else {
			acc.push(row);
			map[row.style_ref_no] = true;
		}
		return acc;
	}, []);
	return result;
}

const pageFilterLgKey = "open_costing_table_attr";

const OpenCostingDashboard = () => {
	const gridRef = useRef();
	const { user } = AuthState();
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { allAttributesObj: styleAllAttributesObj } = PimDashboardState();
	const [tableData, setTableData] = useState([]);
	const [displayImg, setDisplayImg] = useState(true);
	const [tableStatusCount, setTableStatusCount] = useState({
		total: 0,
		pending: 0,
		published: 0,
	});
	const [allColumns, setAllColumns] = useState([]);
	const [allColumnsObj, setAllColumnsObj] = useState({});
	const [publishModal, setPublishModal] = useState(false);
	const [filterModal, setFilterModal] = useState(false);
	const [addColumnModal, setAddColumnModal] = useState(false);
	const [exportModal, setExportModal] = useState(false);

	const defColumns = [...createdByColumns.map((row) => converAttrToDatagridCol(row))];

	const [dgTablColumns, setDgTableColumns] = useState(defColumns);

	const [attrColumns, setAttrColumns] = useState([]);
	const [defAttrColumns, setDefAttrColumns] = useState([]);

	const [filterColumns, setFilterColumns] = useState([]);
	const [filterColumnVal, setFilterColumnVal] = useState({});
	const [offset, setOffset] = useState(0);
	const [recordsLimit] = useState(20);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const styleAttributes = filterRequiredStyleColumns(styleAllAttributesObj);
		const defAttrColumns = [
			...styleAttributes,
			supplierColumn,
			totalQtyColumn,
			applyDiscountingCol,
			poCostColumn,
			finalTotalColumn,
			CreatedAtColumn,
		];
		const allColumns = [...createdByColumns, ...Object.values(publishColumns), ...defAttrColumns];

		const allColumnsObj = allColumns?.reduce((acc, row) => {
			acc[row.key] = row;
			return acc;
		}, {});
		setAllColumns(allColumns);
		setAllColumnsObj(allColumnsObj);
		setAttrColumns(() => {
			if (localStorage.getItem(pageFilterLgKey)) {
				return JSON.parse(localStorage.getItem(pageFilterLgKey))
					?.map((row) => {
						return { ...allColumnsObj?.[row] };
					})
					.map((row) => converAttrToDatagridCol(row));
			} else return defAttrColumns?.map((row) => converAttrToDatagridCol(row));
		});
		setFilterColumns(() => {
			let final = [];
			if (localStorage.getItem(lsFilterkeyStr)) {
				const localStorageObj =
					typeof JSON.parse(localStorage.getItem(lsFilterkeyStr)) === "object"
						? JSON.parse(localStorage.getItem(lsFilterkeyStr))
						: {};
				allColumns?.forEach((row) => {
					if (localStorageObj?.[row?.key]) final.push(row);
				});
				return final;
			}
		});
		setFilterColumnVal(() => {
			if (localStorage.getItem(lsFilterkeyStr)) {
				return JSON.parse(localStorage.getItem(lsFilterkeyStr));
			} else {
				return {};
			}
		});
	}, []);

	const handleCloseFilterModal = () => {
		setFilterModal(false);
	};

	const handleCloseAddColumnModal = () => {
		setAddColumnModal(false);
	};
	const handleCloseExportModal = () => {
		setExportModal(false);
	};

	const handleFilterClick = () => {
		setFilterModal(true);
	};

	const handleAddColumnClick = () => {
		setAddColumnModal(true);
	};

	const fetchtableData = async (offset, limit) => {
		try {
			const extraColumns = attrColumns?.reduce((acc, row) => {
				acc[row.field] = true;
				return acc;
			}, {});

			const { data } = await axios.post(`/api/sourcing/open_costings/${offset}`, {
				filter: filterColumnVal,
				extraColumns,
				limit,
			});
			setTableData((prev) => setStylesCustomFun(prev, data?.result?.rows ?? []));
			setTableStatusCount(data.result.count[0]);
			setOffset((prev) => prev + recordsLimit);
			return data.result || [];
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
			return [];
		}
	};

	const loadMoreData = useCallback(
		_.throttle(async () => {
			if (loading || !hasMore) return;
			if (tableData?.length > 100) return;
			setLoading(true);

			const newRows = await fetchtableData(offset, recordsLimit);

			if (newRows?.rows?.length < recordsLimit) setHasMore(false);
			setLoading(false);
		}, 2000),
		[loading, hasMore, offset]
	);

	useEffect(() => {
		const gridElement = gridRef.current;
		if (!gridElement) return;
		const scrollContainer = gridElement.querySelector(".MuiDataGrid-virtualScroller");
		if (!scrollContainer) return;
		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
			if (scrollHeight - scrollTop <= clientHeight + 100) {
				loadMoreData();
			}
		};
		scrollContainer.addEventListener("scroll", handleScroll);
		return () => {
			scrollContainer.removeEventListener("scroll", handleScroll);
		};
	}, [loadMoreData]);

	useEffect(() => {
		const fetchInitialData = async () => {
			setLoadingOpen(true);
			setDgTableColumns([
				publishTableColumn(handlePublishClick, !allowAccess(custom.COSTING_PUBLISHER, user, departments.SOURCING.key)),
				downLoadTableColumn(handleDownloadCostingClick, true),
				imageTableColumn(styleImageColumn),
				...attrColumns,
				...defColumns,
			]);
			const result = await fetchtableData(0, recordsLimit);
			setTableData(result.rows);
			setTableStatusCount(result.count[0]);
			setOffset((prev) => prev + recordsLimit);
			setHasMore(result.rows.length === recordsLimit);
			setLoadingOpen(false);
		};
		if (allColumns?.length) fetchInitialData();
	}, [filterColumns, attrColumns]);

	const navigate = useNavigate();

	const handleImageSwitchChange = (checked) => {
		setDisplayImg(checked);
	};

	const handleDgRowSelect = async (data) => {
		navigate(`/${selectedOpenCostingPath}?${costingUrlKey}=${data.style_ref_no}`);
	};
	const handleDownloadClick = async () => {
		setExportModal(true);
	};
	const handleDownloadCostingClick = async (e, costData) => {
		e.stopPropagation();
		setLoadingOpen(true);
		try {
			const [result, wh_split_response] = await Promise.all([
				axios.get(`/api/sourcing/costing_full_data/${costData.style_ref_no}`),
				axios.get(`/api/sourcing/split_qty_full_data/${costData.style_ref_no}`),
			]);
			const res = await generateCostingDetailsExcel(result.data.result, wh_split_response.data);
			if (res) {
				setLoadingOpen(false);
			}
		} catch (error) {
			console.log("qwertyuiop", error);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		}
	};
	const handlePublishClick = async (e, styleData) => {
		e.stopPropagation();
		setLoadingOpen(true);
		try {
			const res = await axios.post(`/api/sourcing/publish_costing`, {
				style_ref_no: styleData.style_ref_no,
				published_date: moment().format("YYYY-MM-DD"),
			});
			setTableData((prev) => {
				return prev.map((tableRow) => {
					if (tableRow.style_ref_no === styleData.style_ref_no) {
						return { ...tableRow, ...res.data.result };
					} else return tableRow;
				});
			});
			setTableStatusCount((prev) => ({ ...prev, published: +prev.published + 1, pending: prev.pending - 1 }));
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Costing Published Successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		}
	};

	return (
		<Stack width="100%" pt={1}>
			<Box display="flex" justifyContent="space-around">
				{/* left section */}
				<Stack spacing={1} width="28%">
					<Box display="flex" columnGap={1} pt={1} flexWrap="wrap" alignItems="center" rowGap={"5px"}>
						<Button
							variant="contained"
							size="small"
							startIcon={<FilterAltRounded />}
							onClick={handleFilterClick}
							sx={{ textTransform: "capitalize" }}>
							Filter
						</Button>
						:
						{!filterColumns?.length && (
							<Typography variant="body1" fontSize={16} color={grey[700]}>
								None
							</Typography>
						)}
						{filterColumns?.map((row) => {
							return (
								<Chip
									label={`${row.name} : ${
										Array.isArray(filterColumnVal[row?.key])
											? filterColumnVal[row?.key]?.join(", ")
											: filterColumnVal?.[row?.key]
									}`}
									size="small"
								/>
							);
						})}
					</Box>
					<Box>
						<Button
							variant="contained"
							sx={{ textTransform: "capitalize", mr: 1 }}
							size="small"
							onClick={handleAddColumnClick}
							startIcon={<PlaylistAddRounded />}>
							Manage Columns
						</Button>
						<Button
							variant="contained"
							size="small"
							sx={{ textTransform: "capitalize" }}
							onClick={handleDownloadClick}
							startIcon={<FileDownloadRounded />}>
							Export
						</Button>
					</Box>
				</Stack>
				{/* right section */}
				<Box width="68%"></Box>
			</Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" pt={1}>
				<Box display="flex" alignItems="center">
					<Typography variant="h6">Images</Typography>
					<Switch checked={displayImg} onChange={(e) => handleImageSwitchChange(e.target.checked)} sx={{ mt: 1 }} />
				</Box>
				<Box display="flex" alignItems="center" columnGap={1}>
					<Typography variant="body1">
						Total: <span style={{ color: grey[700] }}>{tableStatusCount.total}</span>
					</Typography>
					<Divider orientation="vertical" flexItem />
					<Typography variant="body1">
						Published: <span style={{ color: green[600] }}>{tableStatusCount.published ?? 0}</span>
					</Typography>
					<Divider orientation="vertical" flexItem />
					<Typography variant="body1" fontWeight={600}>
						Pending: <span style={{ color: "red" }}>{tableStatusCount.pending ?? 0}</span>
					</Typography>
				</Box>
			</Box>

			<Box height="60vh" ref={gridRef}>
				<DataGrid
					rows={tableData ? tableData : []}
					key={displayImg}
					columns={dgTablColumns ?? []}
					sx={{
						// minWidth: "100%"
						border: "none",
						"& .MuiDataGrid-row": {
							// borderBottom: "1px solid lightgray",
							cursor: "pointer",
							border: "none",
						},
						"& .MuiDataGrid-columnHeader": {
							cursor: "pointer",
							background: grey[300],
						},
						"& .MuiDataGrid-columnHeaders": {
							background: grey[300],
							// mb: 2,
							border: "none",
							borderRadius: 1,
							// padding: "10px 0px",
						},
						"& .MuiDataGrid-footer": {
							background: grey[300],
							// mb: 2,
							border: "none",
							borderRadius: 1,
							// padding: "10px 0px",
						},
					}}
					columnHeaderHeight={35}
					rowHeight={displayImg ? 70 : 40}
					getRowId={(row) => row?.style_ref_no}
					onRowClick={(params, e) => handleDgRowSelect(params.row)}
					initialState={{
						columns: {
							columnVisibilityModel: {
								[styleImageColumn.key]: displayImg,
							},
						},
					}}
					disableRowSelectionOnClick
					hideFooter={true}
					pagination={false}
					disableColumnFilter
					disableColumnSelector
					disableAutosize
				/>
				{/* <Box sx={{ minHeight: "20px" }}> */}
				{loading && (
					<Box display="flex" sx={{ background: grey[400], borderRadius: 2, p: 1 }} alignItems="center">
						<Typography variant="body2" color={grey[900]}>
							Loading...
						</Typography>
						<CircularProgress size="18px" sx={{ transform: "scale(0.4)" }} />
					</Box>
				)}
			</Box>

			<FilterModal
				modal={filterModal}
				handleCloseModal={handleCloseFilterModal}
				filterColumns={filterColumns}
				setFilterColumns={setFilterColumns}
				filterColumnVal={filterColumnVal}
				setFilterColumnVal={setFilterColumnVal}
				localStorageStr={lsFilterkeyStr}
				allColumns={allColumns}
			/>
			<AddColumnModal
				modal={addColumnModal}
				handleCloseModal={handleCloseAddColumnModal}
				attrColumns={attrColumns}
				setAttrColumns={setAttrColumns}
				localStorageStr={pageFilterLgKey}
				allColumns={allColumns}
				allColumnsObj={allColumnsObj}
				defaultColumns={defAttrColumns}
			/>
			<DataExportModal modal={exportModal} handleCloseModal={handleCloseExportModal} filterColumnVal={filterColumnVal} />
		</Stack>
	);
};

export default OpenCostingDashboard;
